import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Route, RouteProps, useNavigate } from "react-router";
import { Types } from "Types";
import { Selectors as DossierLocationSelectors } from "../../redux/DossierLocation";
import { Selectors as UtilisateursSelectors } from "../../redux/Utilisateurs";
import {
  Profil,
  Utilisateur,
  UtilisateurRoleEnum,
} from "../../services/Api/swagger";
import {
  getUsername,
  isAuthenticated,
  isTokenExpired,
  loginIfRequired,
  updateToken,
} from "../../services/Authentification/authentification";
import { isPathAuthorizedForUser } from "../../services/Route";
import { history } from "../../redux/configureStore";
import Spinner from "../spinner/Spinner";
import { useSelector } from "react-redux";
import Api, { IApi } from "../../services/Api";
import { getApiEndpoint } from "../../services/Config";
import { useQuery } from "react-query";
import { ActionCreators, Selectors } from "../../redux/Utilisateurs";
import { useDispatch } from "react-redux";
import LoadingErrorBoundary from "../LoadingBoundary";
import { Selectors as AdbSelector } from "../../redux/AdministrateurDeBien";

interface CustomProps {
  children: JSX.Element;
}

type Props = RouteProps & CustomProps;

const PrivateRoute: React.FC<Props> = ({ children }: CustomProps) => {
  const [state, setState] = useState({
    authenticated: false,
    error: undefined as any,
    unauthorized: false,
  });

  const infoProfil = useSelector((state: Types.RootState) =>
    UtilisateursSelectors.getProfilUtilisateur(state),
  );
  const refBien = useSelector((state: Types.RootState) =>
    DossierLocationSelectors.getSelectedDossier(state),
  );
  const statutDossier = useSelector((state: Types.RootState) =>
    DossierLocationSelectors.getStatutDossier(state),
  );

  const refDossier =
    refBien ||
    new URLSearchParams(document.location.search).get("refDossier") ||
    undefined;

  const path = window?.location?.pathname;
  useEffect(() => {
    if (!state.authenticated) {
      loginIfRequired()
        .then((authenticated) => {
          if (authenticated) {
            const path = window?.location?.pathname;
            // Si on n'a pas de profil de chargé et qu'on n'est pas sur l'url du dashboard
            // on a une redirection pour déclencher le chargement des profils

            setState({
              ...state,
              authenticated,
            });
          } else {
            setState({
              ...state,
              authenticated,
            });
          }
        })
        .catch((error) => {
          setState({
            ...state,
            error,
          });
        });
    } else {
      setState({
        ...state,
        authenticated: true,
      });
    }
  }, []);

  useEffect(() => {
    if (path && infoProfil && isAuthenticated()) {
      const authorized =
        path &&
        isPathAuthorizedForUser(path, infoProfil, refDossier, statutDossier);
      setState({
        ...state,
        unauthorized: !authorized,
      });
    }
  }, [infoProfil, refDossier, path, statutDossier]);
  // useEffect(() => {
  //   if (state.authenticated) {
  //     const path = window?.location?.pathname;
  //     const authorized = isPathAuthorizedForUser(
  //       path,
  //       infoProfil,
  //       refBien,
  //       statutDossier
  //     );
  //     setState({
  //       ...state,
  //       unauthorized: !authorized,
  //     });
  //   }
  // }, [state.authenticated]);

  const api: IApi = Api.create(getApiEndpoint());
  const username = getUsername();
  const dispatch = useDispatch();
  const {
    isFetching: isFetchingUser,
    error: errorUser,
    refetch: refetchUser,
    data: userData,
    isSuccess: isSuccessUser,
  } = useQuery<Utilisateur, any>(
    ["getUser", username],
    () => (username && api?.getUtilisateurByUsername(username)) || undefined,
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        dispatch(ActionCreators.getUtilisateurConnecteByUsername.success(data));
      },
      onError: (error) => {
        dispatch(
          ActionCreators.getUtilisateurConnecteByUsername.failure(error),
        );
      },
    },
  );

  const navigate = useNavigate();
  const id =
    userData &&
    userData.detailAdbProfilSites &&
    userData.detailAdbProfilSites.length > 0
      ? userData.detailAdbProfilSites[0].profil
      : userData && userData.profilBesse;

  const idAdb = useSelector((state: Types.RootState) =>
    AdbSelector.getAdbId(state),
  );
  const {
    isFetching: fetchingProfilById,
    error: errorProfilById,
    isSuccess: isSuccessProfilById,
  } = useQuery<Profil | undefined, any>(
    ["getProfilById", id],
    () => (id && api?.getProfilById(id)) || undefined,
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isSuccessUser,
      onSuccess: (data) => {
        data && dispatch(ActionCreators.getProfil.success(data));
        // Si l'on vient d'un lien qui contient l'url d'un dossier (après authentification) on redirige vers la synthèse
        const refPath = new URLSearchParams(document.location.search).get(
          "refDossier",
        );
        if (
          userData?.role === UtilisateurRoleEnum.BESSE &&
          !idAdb &&
          !refPath
        ) {
          // Si l'on est un admin BESSE on redirige vers la page adb
          navigate("/mes-administrateurs-de-biens");
        }
      },
      onError: (error) => {
        dispatch(ActionCreators.getProfil.failure(error));
      },
    },
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const tokenExpired = isTokenExpired();
      if (tokenExpired) {
        updateToken();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (state.error) {
    return <div>Impossible de se connecter !</div>;
  }

  if (state.authenticated) {
    if (state.unauthorized) {
      window.location.href = "/accesInterdit";
      return null;
    }
    return (
      <LoadingErrorBoundary
        loading={isFetchingUser || fetchingProfilById}
        error={errorUser || errorProfilById}
        onRetry={refetchUser}
      >
        {children}
      </LoadingErrorBoundary>
    );
  }

  return <Spinner />;
};

export default PrivateRoute;
